import React, { useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, X, ArrowRight, ArrowLeft, Sparkles, Check, Palette, ImageIcon, Search, HelpCircle, Globe, CheckCircle2, AlertTriangle, Wand2 } from "lucide-react";

const STEPS = [
  { key: "template", label: "Template", icon: Palette, title: "Choisissez votre style", desc: "Le design de votre page — changez-le à tout moment." },
  { key: "logo", label: "Logo", icon: ImageIcon, title: "Votre logo", desc: "Il s'affiche en haut de votre page, comme un vrai site à votre marque." },
  { key: "seo", label: "SEO", icon: Search, title: "Contenu & référencement", desc: "Quelques mots clés pour être trouvé sur Google — l'IA rédige le reste." },
  { key: "faq", label: "FAQ", icon: HelpCircle, title: "Votre FAQ", desc: "Générée par l'IA, synchronisée avec vos données juridiques, conditions et options." },
  { key: "publish", label: "Publier", icon: Globe, title: "Mise en ligne", desc: "Votre page est prête — publiez-la et partagez le lien." },
];

export default function VendreWizard({ settings, themes, legacyTheme, onClose, onFinished }) {
  const shop = settings.shop || {};
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [theme, setTheme] = useState(legacyTheme[shop.theme] || shop.theme || "premium");
  const [keywords, setKeywords] = useState(shop.seo?.keywords || "");
  const [description, setDescription] = useState(shop.seo?.description || "");
  const [genSeo, setGenSeo] = useState(false);
  const [faq, setFaq] = useState(shop.faq || []);
  const [genFaq, setGenFaq] = useState(false);
  const [published, setPublished] = useState(!!shop.published);
  const [pageUrl, setPageUrl] = useState(shop.slug ? `${window.location.origin}/p/${shop.slug}` : null);

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const logo = settings.company?.logo_data_url;

  const saveStep = async () => {
    if (current.key === "template") await api.put("/vendor/settings", { shop: { theme } });
    if (current.key === "seo") await api.put("/vendor/settings", { shop: { seo: { keywords, description } } });
    if (current.key === "faq" && faq.length > 0) await api.put("/vendor/settings", { shop: { faq } });
  };

  const next = async () => {
    setSaving(true);
    try {
      await saveStep();
      if (isLast) {
        await api.put("/vendor/settings", { shop: { wizard_done: true } });
        toast.success("Configuration terminée 🎉");
        onFinished();
      } else setStep(step + 1);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setSaving(false);
  };

  const generateSeo = async () => {
    setGenSeo(true);
    try {
      const { data } = await api.post("/vendor/shop/seo/generate", { keywords });
      setDescription(data.description);
      toast.success("Description générée");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Génération impossible"); }
    setGenSeo(false);
  };

  const generateFaq = async () => {
    setGenFaq(true);
    try {
      const { data } = await api.post("/vendor/shop/faq/generate");
      setFaq(data.faq || data.items || data);
      toast.success("FAQ générée");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Génération impossible"); }
    setGenFaq(false);
  };

  const publish = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/vendor/shop/publish");
      setPublished(true);
      if (data.slug) setPageUrl(`${window.location.origin}/p/${data.slug}`);
      toast.success("Votre page est en ligne 🎉");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-6" data-testid="vendre-wizard">
      <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-3xl max-h-[92vh] flex flex-col rounded-3xl bg-white shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 sm:px-8 pt-6 pb-4 border-b border-slate-100 bg-gradient-to-r from-violet-50/70 to-white">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold tracking-wider uppercase">
                <Sparkles className="h-3 w-3" /> Configuration guidée — Votre page web
              </div>
              <h2 className="font-heading text-xl sm:text-2xl font-semibold text-slate-900 mt-2">{current.title}</h2>
              <p className="text-sm text-slate-500 mt-1">{current.desc}</p>
            </div>
            <button onClick={onClose} className="p-2 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors shrink-0" data-testid="vendre-wizard-close" aria-label="Fermer">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex items-center gap-1.5 mt-4">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const state = i < step ? "done" : i === step ? "active" : "todo";
              return (
                <React.Fragment key={s.key}>
                  {i > 0 && <div className={`h-px flex-1 ${i <= step ? "bg-emerald-400" : "bg-slate-200"}`} />}
                  <button type="button" onClick={() => setStep(i)}
                    className={`relative flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold transition-all cursor-pointer ${
                      state === "active" ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/40 scale-105 ring-2 ring-emerald-300"
                      : state === "done" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                      : "bg-slate-100 text-slate-400 hover:bg-slate-200 hover:text-slate-600"}`}
                    data-testid={`vendre-wizard-step-${s.key}`}>
                    {state === "done" ? <Check className="relative h-3 w-3" /> : <Icon className="relative h-3 w-3" />}
                    <span className="relative hidden sm:inline">{s.label}</span>
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-6">
          {current.key === "template" && (
            <div className="grid sm:grid-cols-2 gap-4">
              {themes.map((t) => {
                const active = theme === t.key;
                return (
                  <button key={t.key} onClick={() => setTheme(t.key)}
                    className={`text-left rounded-2xl border-2 p-4 transition-all ${active ? "border-violet-500 bg-violet-50/50 shadow-lg shadow-violet-100" : "border-slate-200 bg-white hover:border-violet-300"}`}
                    data-testid={`wizard-theme-${t.key}`}>
                    {t.preview}
                    <div className="mt-3 flex items-center justify-between">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{t.label}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{t.desc}</div>
                      </div>
                      {active && <CheckCircle2 className="h-5 w-5 text-violet-600 shrink-0" />}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {current.key === "logo" && (
            logo ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-6 flex items-center gap-4" data-testid="wizard-logo-ok">
                <img src={logo} alt="Logo" className="h-16 w-16 rounded-xl object-contain bg-white p-1.5 border border-emerald-200" />
                <div>
                  <div className="text-sm font-bold text-emerald-800 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Votre logo est déjà configuré</div>
                  <div className="text-xs text-emerald-700 mt-1">Il provient de vos Paramétrages → Entreprise. Pour le changer : <a href="/vendor/parametrages?section=company" className="underline font-semibold">modifier mon logo</a></div>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-6 flex items-start gap-3" data-testid="wizard-logo-missing">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-800">
                  <span className="font-bold">Aucun logo pour le moment.</span>
                  <p className="text-xs mt-1.5">Votre logo se configure une seule fois, dans vos paramétrages d'entreprise — il sera repris automatiquement sur votre page, vos devis et vos contrats.</p>
                  <a href="/vendor/parametrages?section=company" className="mt-3 inline-flex items-center gap-1.5 px-4 h-9 rounded-xl bg-amber-600 text-white text-xs font-bold hover:bg-amber-700 transition-colors" data-testid="wizard-logo-link">
                    Ajouter mon logo <ArrowRight className="h-3 w-3" />
                  </a>
                </div>
              </div>
            )
          )}

          {current.key === "seo" && (
            <div className="space-y-5">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 block">Vos mots clés</label>
                <input value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="location de voiture Guadeloupe, SUV, aéroport…"
                  className="w-full h-11 px-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-violet-400" data-testid="wizard-seo-keywords" />
              </div>
              <div className="rounded-xl bg-violet-50/60 border border-violet-100 p-4 flex items-start gap-2.5" data-testid="wizard-seo-advice">
                <Sparkles className="h-4 w-4 text-violet-500 mt-0.5 shrink-0" />
                <div className="text-xs text-slate-600 leading-relaxed">
                  <span className="font-bold text-slate-800">Conseil Junago :</span> pour le référencement, mettez en avant votre <span className="font-semibold">activité principale + votre ville</span>. Exemple : <span className="font-semibold">« location de voiture Guadeloupe »</span>. Ajoutez 2-3 mots clés qui vous distinguent (aéroport, SUV, luxe, utilitaire…).
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Description de votre page</label>
                  <button onClick={generateSeo} disabled={genSeo} className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg bg-violet-600 text-white text-[11px] font-bold hover:bg-violet-500 transition-colors disabled:opacity-50" data-testid="wizard-seo-generate">
                    {genSeo ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />} Générer avec l'IA
                  </button>
                </div>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Générée à partir de vos informations : entreprise, villes, véhicules et mots clés."
                  className="w-full p-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-violet-400" data-testid="wizard-seo-description" />
              </div>
            </div>
          )}

          {current.key === "faq" && (
            <div className="space-y-4">
              <div className="rounded-xl bg-violet-50/60 border border-violet-100 p-4 flex items-start gap-2.5">
                <Sparkles className="h-4 w-4 text-violet-500 mt-0.5 shrink-0" />
                <div className="text-xs text-slate-600 leading-relaxed">
                  Votre FAQ est <span className="font-bold text-slate-800">synchronisée avec vos données</span> : textes juridiques (CGV), conditions de location (caution, franchise, âge…), options et kilométrage. L'IA n'invente rien — elle reformule vos règles pour vos clients.
                </div>
              </div>
              <button onClick={generateFaq} disabled={genFaq} className="inline-flex items-center gap-2 px-5 h-11 rounded-xl bg-violet-600 text-white text-xs font-bold hover:bg-violet-500 transition-colors disabled:opacity-50" data-testid="wizard-faq-generate">
                {genFaq ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} {faq.length > 0 ? "Régénérer la FAQ" : "Générer ma FAQ avec l'IA"}
              </button>
              {faq.length > 0 && (
                <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="wizard-faq-list">
                  {faq.map((f, i) => (
                    <div key={i} className="rounded-xl border border-slate-200 p-3">
                      <div className="text-xs font-bold text-slate-800">{f.q}</div>
                      <div className="text-xs text-slate-500 mt-1">{f.a}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {current.key === "publish" && (
            <div className="space-y-4">
              {published ? (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-6" data-testid="wizard-published">
                  <div className="text-sm font-bold text-emerald-800 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Votre page est en ligne</div>
                  {pageUrl && <a href={pageUrl} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-emerald-700 underline font-semibold">{pageUrl}</a>}
                  {pageUrl && (
                    <div className="mt-4 pt-4 border-t border-emerald-200/60">
                      <div className="text-xs font-bold text-emerald-800 mb-2.5">🚀 Partagez-la dès maintenant :</div>
                      <div className="flex flex-wrap gap-2" data-testid="wizard-share-row">
                        <a href={`https://wa.me/?text=${encodeURIComponent(`Réservez votre voiture en ligne : ${pageUrl}`)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-[#25D366] text-white text-xs font-bold hover:opacity-90 transition-opacity" data-testid="wizard-share-whatsapp">
                          <i className="fab fa-whatsapp text-sm" /> WhatsApp
                        </a>
                        <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-[#1877F2] text-white text-xs font-bold hover:opacity-90 transition-opacity" data-testid="wizard-share-facebook">
                          <i className="fab fa-facebook text-sm" /> Facebook
                        </a>
                        <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-[#0A66C2] text-white text-xs font-bold hover:opacity-90 transition-opacity" data-testid="wizard-share-linkedin">
                          <i className="fab fa-linkedin text-sm" /> LinkedIn
                        </a>
                        <button onClick={() => { navigator.clipboard.writeText(pageUrl); toast.success("Lien copié !"); }} className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-white border border-emerald-200 text-emerald-700 text-xs font-bold hover:bg-emerald-50 transition-colors" data-testid="wizard-share-copy">
                          Copier le lien
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-2xl border border-slate-200 p-6 text-center">
                  <Globe className="h-8 w-8 text-violet-500 mx-auto" />
                  <div className="text-sm font-bold text-slate-900 mt-3">Tout est prêt !</div>
                  <p className="text-xs text-slate-500 mt-1">Publiez votre page pour obtenir votre lien et commencer à encaisser des réservations.</p>
                  <button onClick={publish} disabled={saving} className="mt-4 inline-flex items-center gap-2 px-6 h-11 rounded-xl bg-violet-600 text-white text-sm font-bold hover:bg-violet-500 transition-colors disabled:opacity-50" data-testid="wizard-publish-btn">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />} Publier ma page
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 sm:px-8 py-4 border-t border-slate-100 flex items-center justify-between gap-3">
          <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
            className="inline-flex items-center gap-1.5 px-4 h-10 rounded-xl border border-slate-200 text-xs font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition-colors" data-testid="vendre-wizard-prev">
            <ArrowLeft className="h-3.5 w-3.5" /> Précédent
          </button>
          <button onClick={next} disabled={saving}
            className="inline-flex items-center gap-1.5 px-6 h-10 rounded-xl bg-slate-900 text-white text-xs font-bold hover:bg-slate-800 disabled:opacity-50 transition-colors" data-testid="vendre-wizard-next">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : isLast ? <>Terminer <Check className="h-3.5 w-3.5" /></> : <>Continuer <ArrowRight className="h-3.5 w-3.5" /></>}
          </button>
        </div>
      </div>
    </div>
  );
}
